import {
  Box,
  Button,
  Link,
  ListItem,
  Text,
  UnorderedList,
  VStack
} from "@chakra-ui/react"
import { CloudArrowsFillIcon, Group2Icon } from "@pathwright/pathicons"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import { useEffect, useRef } from "react"
import { RESOURCE_TYPE_COLLECTION } from "../..//resource/constants"
import { CohortFragment } from "../../api/generated"
import Tip from "../../tips/Tip"
import { useTips } from "../../tips/TipsProvider"
import { flattenEdges } from "../../utils/apollo"
import { getGroupUrl } from "../../utils/urls"
import SuggestedAction from "../sync/SuggestedAction"
import { Path } from "../sync/types"

export const TeachModeBlankSlate = ({
  onDismiss,
  cohortId,
  offering,
  resource
}: {
  onDismiss: () => void
  cohortId: number
  offering?: any
  resource?: any
}) => {
  const { tc } = useTranslate()

  return (
    <Box
      maxW="md"
      mx="auto"
      sx={{
        ".dismiss-tip": {
          display: "none"
        }
      }}
    >
      <Tip
        colorScheme="dark"
        layout="dismissable"
        tip={{
          id: `tip-cohort-${cohortId}-teach-path-empty`,
          title: tc("Guide people through your Path"),
          content: (
            <VStack gap={6} align="start">
              <Text fontSize="16px" m={0} color="rgba(255,255,255,0.9)">
                {tc(
                  "After you design the {{name}} Path and people join, Teach tools enable you to",
                  {
                    name: offering?.name || resource?.name
                  }
                )}
                :
              </Text>
              <UnorderedList
                spacing={1}
                fontSize="16px"
                color="rgba(255,255,255,0.9)"
              >
                <ListItem>
                  {tc("See how everyone’s progressing at a glance")}.
                </ListItem>
                <ListItem>
                  {tc(
                    "Schedule start and due dates with reminders to help them stay on track"
                  )}
                  .
                </ListItem>
                <ListItem>
                  {tc(
                    "Quickly scan for questions and reviews that need your attention"
                  )}
                  .
                </ListItem>
                <ListItem>{tc("And more")}...</ListItem>
              </UnorderedList>
              <Text
                textAlign="left"
                m={0}
                fontSize="16px"
                color="rgba(255,255,255,0.9)"
              >
                <Link href="https://help.pathwright.com/en/articles/6496765-teacher-role">
                  {tc("Learn more about the Teach tools")}
                </Link>
              </Text>
            </VStack>
          )
        }}
        renderButton={() => {
          return (
            <Button cursor="pointer" onClick={() => onDismiss()}>
              {tc("Design Path")}
            </Button>
          )
        }}
      />
    </Box>
  )
}

type SourcePathBlankSlateProps = {
  source: {
    name: string
    id: number
    resource?: {
      id: number
      slug: string
      name: string
      resource_type: number
    }
  }
  onLaunchCreateCohort: () => void
  onDismiss: () => void
  cohorts: CohortFragment[]
  cohort?: CohortFragment
  singleCohortPath?: Path
}

const tipId = "source-path-blank-slate"

export const getBlankSlateTipId = (resourceId: number) => {
  return `${tipId}-${resourceId}`
}

const pathHasSteps = (singleCohortPath?: Path, isCollection?: boolean) => {
  if (!singleCohortPath) return false

  return !!flattenEdges(singleCohortPath?.items)?.find((item: any) =>
    isCollection ? item.type === "resource" : item.type === "step"
  )
}

export const useGetShowBlankSlateTip = ({
  isEmptySource,
  numCohorts,
  resource,
  singleCohortPath
}: {
  isEmptySource: boolean
  numCohorts: number
  resource: any
  singleCohortPath?: Path
}) => {
  const { getTipStatus } = useTips()

  const { id: resourceId, resource_type } = resource

  const isCollection = resource_type === RESOURCE_TYPE_COLLECTION

  // If not the Source Path or if it has Steps, don't show the tip
  // If there are multiple cohorts, don't show the tip
  if (!isEmptySource || numCohorts > 1) return false

  const tipIsDismissed =
    getTipStatus(getBlankSlateTipId(resourceId)) === "dismissed"
  const cohortPathHasSteps = pathHasSteps(singleCohortPath, isCollection)

  // The DesignSourceTip is shown when there are no Path items in the single Cohort Path,
  // and it is dismissable
  const showDesignTip = !cohortPathHasSteps && !tipIsDismissed

  // If the Source Path is empty and the Cohort Path has Steps, show the SyncWithSourceTip
  const showSyncTip = cohortPathHasSteps

  return showDesignTip || showSyncTip
}

// The purpose of this component is to show a tip to the user when they have a Source Path that is empty
// and do not yet have a cohort. Since the Source Path is empty and there's no cohort,
// the Designer is prompted to consider starting with a Cohort or staying in the Source Path.
const DesignSourceTip = (props: SourcePathBlankSlateProps) => {
  const { source, onDismiss, onLaunchCreateCohort, cohorts } = props
  const { tc, Trans } = useTranslate()

  const sourcePathButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (sourcePathButtonRef.current) {
      sourcePathButtonRef.current.focus()
    }
  }, [sourcePathButtonRef?.current])

  const { name: cohortName, resource } = source

  const existingCohort = cohorts?.[0]

  return (
    <>
      <Tip
        tip={{
          id: getBlankSlateTipId(resource?.id!),
          title: tc("Sync changes across multiple Cohorts"),
          content: (
            <VStack spacing={1}>
              <Text color="rgba(255,255,255,0.9)" mb={4} mt={0}>
                <Trans i18nKey="sourcePathBlankSlateTip">
                  If multiple Cohorts will take the {cohortName} Path, use this
                  Source Path sync all Cohorts with the best, latest version.{" "}
                  <a
                    target="_blank"
                    href="https://help.pathwright.com/en/articles/5159775-sync-how-to-keep-your-content-in-sync-across-multiple-cohorts"
                  >
                    Learn more about Source Paths & Cohorts
                  </a>
                </Trans>
                .
              </Text>
              <Text color="rgba(255,255,255,0.9)" my={0}>
                {tc(
                  "If you only need one Cohort for now, you can skip the Source Path and use it later once you add two or more Cohorts."
                )}
              </Text>
            </VStack>
          )
        }}
        colorScheme="dark"
        defaultState="expanded"
        layout={"dismissable"}
        renderButton={({ onDismiss: onDismissLSTip }) => {
          return (
            <VStack spacing={2}>
              <Button
                as="a"
                href={
                  existingCohort && resource?.slug
                    ? getGroupUrl(resource?.slug, existingCohort.id)
                    : undefined
                }
                bg="rgba(255,255,255,0.2)"
                color="white"
                border="1px solid transparent"
                textDecoration="none !important"
                _hover={{
                  bg: "whiteAlpha.300",
                  color: "white",
                  textDecoration: "none"
                }}
                cursor="pointer"
                onClick={() => {
                  if (existingCohort) return
                  // Don't need to dismiss the tip.
                  // Once the cohort is created with content, the Tip won't appear.
                  // onDismissLSTip() // adds LS value to dismiss the tip
                  onLaunchCreateCohort() // Opens the Create Cohort modal
                }}
                leftIcon={<Group2Icon size="1.2em" color="currentColor" />}
                sx={{
                  "&:focus": {
                    border: "1px solid var(--chakra-colors-blue-500)"
                  }
                }}
              >
                {tc("Design Cohort Path")}
              </Button>
              <Button
                bg="rgba(255,255,255,0.2)"
                color="white"
                cursor="pointer"
                variant="outline"
                borderColor="transparent"
                _hover={{
                  bg: "whiteAlpha.300",
                  color: "white",
                  textDecoration: "none"
                }}
                onClick={() => {
                  onDismissLSTip() // adds LS value to dismiss the tip
                  onDismiss!() // sets the local state in Resource Path to dismissed
                }}
                sx={{
                  "&:focus": {
                    border: "1px solid var(--chakra-colors-blue-500)"
                  },
                  svg: {
                    marginBottom: "-2px"
                  }
                }}
                leftIcon={
                  <CloudArrowsFillIcon size="1.2em" color="currentColor" />
                }
                ref={sourcePathButtonRef}
              >
                {tc("Design Source Path")}
              </Button>
            </VStack>
          )
        }}
      />
    </>
  )
}

// The purpose of this component is to show a tip to the user when they have a Source Path that is empty
// but already have a Cohort with content in it. The next step is to sync the Cohort with the Source Path
// before they can add content to the Source Path.
const SyncWithSourceTip = (props: SourcePathBlankSlateProps) => {
  const { source, cohort } = props
  const { resource } = source

  const { tc, Trans } = useTranslate()

  const cohortName = cohort?.name

  if (!cohort?.id || !source?.id || !resource?.slug) return null

  const syncURL = `${getGroupUrl(
    resource?.slug,
    source?.id!
  )}sync?dir=backward&cohortId=${cohort?.id}`

  return (
    <Box>
      <Tip
        tip={{
          id: getBlankSlateTipId(resource?.id),
          title: tc("Sync changes across multiple Cohorts"),
          content: (
            <VStack spacing={1}>
              <Box>
                <Text color="rgba(255,255,255,0.9)" mt={0} mb={4}>
                  <Trans i18nKey="syncWithSourcePathBlankSlateTip">
                    Use this Source Path to keep multiple Cohorts synced with
                    the best, latest version of your Path.{" "}
                    <Link href="https://help.pathwright.com/en/articles/5159775-sync-how-to-keep-your-content-in-sync-across-multiple-cohorts">
                      Learn more about Source Paths & Cohorts.
                    </Link>
                  </Trans>
                </Text>
                <Text
                  color="rgba(255,255,255,0.9)"
                  my={0}
                  sx={{
                    ".chakra-link": {
                      fontWeight: "bold"
                    }
                  }}
                >
                  <Trans
                    i18nKey="syncWithSourcePathBlankSlateTip2"
                    cohort={cohortName}
                  >
                    To get started, first sync the latest Path from the{" "}
                    <Link href={getGroupUrl(resource?.slug, cohort?.id!)}>
                      {{ cohort: cohortName }} Cohort
                    </Link>{" "}
                    with the Source Path:
                  </Trans>
                </Text>
              </Box>
            </VStack>
          )
        }}
        colorScheme="dark"
        defaultState="expanded"
        layout={"default"}
        renderButton={() => {
          return (
            <Box
              sx={{
                ".chakra-button": {
                  bg: "transparent",
                  color: "white",
                  borderColor: "whiteAlpha.500",
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  height: "auto",
                  padding: "0.5em 1em",
                  verticalAlign: "middle"
                },
                ".chakra-button:hover": {
                  bg: "whiteAlpha.200",
                  color: "white"
                },
                svg: {
                  marginBottom: "-2px"
                },
                ".chakra-button__icon": {
                  lineHeight: 1,
                  display: "inline-block",
                  verticalAlign: "middle",
                  marginBottom: "-3px"
                }
              }}
              maxW={["240px", "300px"]}
              overflow="hidden"
            >
              <SuggestedAction
                label={tc(`Sync {{cohort}} Path with Source Path`, {
                  cohort: cohortName
                })}
                icon="cloud-arrows-fill"
                url={syncURL}
              />
            </Box>
          )
        }}
      />
    </Box>
  )
}

const SourcePathBlankSlate = (props: SourcePathBlankSlateProps) => {
  const { singleCohortPath, source } = props

  const isCollection =
    source.resource?.resource_type === RESOURCE_TYPE_COLLECTION

  const isCohortBlankSlate = !pathHasSteps(singleCohortPath, isCollection)

  const TipComponent = isCohortBlankSlate ? DesignSourceTip : SyncWithSourceTip

  return (
    <Box
      maxW="500px"
      mx="auto"
      mt={2}
      sx={{
        ".dismiss-tip": {
          display: "none"
        }
      }}
    >
      <TipComponent {...props} />
    </Box>
  )
}

export default SourcePathBlankSlate
